import { format } from 'date-fns'

import { roundAmount } from '@features/reconciliation/utils/roundAmount'

import type { ReviewEntryDataFormValues, ReviewEntryLineItemFormValues } from '../types/getReviewEntryDataFormSchema'
import type { ReviewEntryItem } from '../types/reviewEntryItem'
import type { ReviewEntryLine } from '../types/reviewEntryLine'
import { formatReviewEntryAccountLabel } from './formatReviewEntryAccountLabel'
import { getReviewEntryId } from './getReviewEntryId'
import { getReviewEntrySourceFingerprint } from './getReviewEntrySourceFingerprint'
import { getReviewEntryDraft } from './reviewEntryDraftStorage'

const formatVatRateLabel = (taxRate: number | undefined, fallback?: string): string | undefined => {
  if (taxRate == null) {
    return fallback
  }

  const percent = Number.parseFloat((taxRate * 100).toFixed(2))
  return `${percent}%`
}

const roundOptionalAmount = (amount: number | undefined): number | undefined =>
  amount == null ? undefined : roundAmount(amount)

const getDraftAccountLabel = (
  draftLine: Partial<ReviewEntryLineItemFormValues>,
  originalLine?: ReviewEntryLine,
): string | undefined => {
  if (originalLine != null && originalLine.accountId === draftLine.accountId) {
    return formatReviewEntryAccountLabel(originalLine)
  }

  return formatReviewEntryAccountLabel({
    accountName: draftLine.accountName,
    accountNumber: draftLine.accountNumber,
  })
}

const getDraftProcessingDecision = (
  draft: Partial<ReviewEntryDataFormValues>,
  originalProcessingDecision: ReviewEntryItem['processingDecision'],
): ReviewEntryItem['processingDecision'] => {
  if (draft.supplier === undefined) {
    return originalProcessingDecision
  }

  const supplierId = draft.supplier?.id?.trim()

  if (!supplierId) {
    return undefined
  }

  return {
    thirdParty: {
      candidates: [{ billyContactId: supplierId, status: 'selected' }],
    },
  }
}

const mapDraftLineToEntryLine = (
  draftLine: Partial<ReviewEntryLineItemFormValues>,
  originalLine?: ReviewEntryLine,
): ReviewEntryLine => {
  const accountUnchanged = originalLine != null && originalLine.accountId === draftLine.accountId
  const vatRateUnchanged = originalLine != null && originalLine.taxRateId === draftLine.vatRateId

  return {
    account: getDraftAccountLabel(draftLine, originalLine),
    accountId: draftLine.accountId,
    accountName: accountUnchanged ? originalLine.accountName : draftLine.accountName,
    accountNumber: draftLine.accountNumber,
    amountExclVat: roundOptionalAmount(draftLine.amountExclVat),
    amountInclVat: roundOptionalAmount(draftLine.amountInclVat),
    description: draftLine.description,
    id: originalLine?.id,
    side: draftLine.side,
    taxAmount: roundOptionalAmount(draftLine.vatAmount),
    taxRateId: draftLine.vatRateId,
    taxRateLabel: vatRateUnchanged
      ? originalLine.taxRateLabel
      : formatVatRateLabel(draftLine.taxRate, originalLine?.taxRateLabel),
    vatAccountNumber: draftLine.vatAccountNumber,
    vatAmount: roundOptionalAmount(draftLine.vatAmount),
  }
}

const getDraftReceiptDate = (receiptDate: ReviewEntryDataFormValues['receiptDate'] | undefined): string | undefined => {
  if (receiptDate instanceof Date && !Number.isNaN(receiptDate.getTime())) {
    return format(receiptDate, 'yyyy-MM-dd')
  }

  return undefined
}

const getOverlayAmountInclVat = (
  item: ReviewEntryItem,
  entryLines: ReviewEntryLine[],
  hasDraftLines: boolean,
): number | undefined => {
  if (!hasDraftLines) {
    return item.amountInclVat
  }

  const lineTotals = entryLines
    .map((line) => line.amountInclVat ?? line.amountInclTax)
    .filter((amount): amount is number => amount != null)

  if (!lineTotals.length) {
    return item.amountInclVat
  }

  return roundAmount(lineTotals.reduce((sum, amount) => sum + amount, 0))
}

const mergeReviewEntryItemWithDraft = (
  item: ReviewEntryItem,
  draft: Partial<ReviewEntryDataFormValues>,
): ReviewEntryItem => {
  const originalLines = item.entryLines ?? []
  const draftLines = draft.entryLines
  const entryLines =
    draftLines == null
      ? originalLines
      : draftLines.map((draftLine, lineIndex) => mapDraftLineToEntryLine(draftLine, originalLines[lineIndex]))

  return {
    ...item,
    amountInclVat: getOverlayAmountInclVat(item, entryLines, draftLines != null),
    currencyId: draft.currencyId ?? item.currencyId,
    entryDate: getDraftReceiptDate(draft.receiptDate) ?? item.entryDate,
    entryLines,
    processingDecision: getDraftProcessingDecision(draft, item.processingDecision),
    supplierName: draft.supplier === null ? undefined : (draft.supplier?.name ?? item.supplierName),
  }
}

export const applyReviewEntryDraftToItem = (
  item: ReviewEntryItem,
  entryIndex: number,
  organizationId?: string,
): ReviewEntryItem => {
  if (!organizationId) {
    return item
  }

  const draft = getReviewEntryDraft({
    entryId: getReviewEntryId(item, entryIndex),
    organizationId,
    sourceFingerprint: getReviewEntrySourceFingerprint(item),
  })

  if (!draft) {
    return item
  }

  return mergeReviewEntryItemWithDraft(item, draft)
}

export const applyReviewEntryDraftsToItems = (items: ReviewEntryItem[], organizationId?: string): ReviewEntryItem[] =>
  items.map((item, entryIndex) => applyReviewEntryDraftToItem(item, entryIndex, organizationId))
